const PREFIX = '#/c/';
const MODES = ['continuable', 'one-shot'];
/**
 * Read a selection from a location fragment.
 * @param hash - `location.hash`, with or without the leading `#`.
 * @returns the linked selection, or `undefined` when the fragment is not a conversation link.
 */
export function parseSessionHash(hash) {
    const fragment = hash.startsWith('#') ? hash : `#${hash}`;
    if (!fragment.startsWith(PREFIX))
        return undefined;
    const [rawId = '', query = ''] = fragment.slice(PREFIX.length).split('?');
    let sessionId;
    try {
        sessionId = decodeURIComponent(rawId);
    }
    catch {
        return undefined;
    }
    if (sessionId === '' || sessionId.includes('/'))
        return undefined;
    const params = new URLSearchParams(query);
    const parent = params.get('parent');
    const mode = MODES.find(candidate => candidate === params.get('mode'));
    if (parent === null || parent === '' || mode === undefined)
        return { sessionId: sessionId };
    return {
        sessionId: sessionId,
        subagentAddress: {
            parentSessionId: parent,
            childSessionId: sessionId,
            mode,
        },
    };
}
/**
 * Format the fragment addressing one selection.
 * @param selection - the session, plus its direct-parent address for a sub-conversation.
 * @returns the fragment, leading `#` included.
 */
export function formatSessionHash(selection) {
    const base = `${PREFIX}${encodeURIComponent(selection.sessionId)}`;
    const address = selection.subagentAddress;
    if (address === undefined)
        return base;
    const params = new URLSearchParams({ parent: address.parentSessionId, mode: address.mode });
    return `${base}?${params.toString()}`;
}
/** The browser objects the link follows; absent outside a browser (tests, server render). */
function browser() {
    if (typeof window === 'undefined')
        return undefined;
    return { location: window.location, history: window.history };
}
/**
 * The selection the current page address links to.
 * @returns the linked selection, or `undefined` outside a browser or without a conversation link.
 */
export function readLocationSelection() {
    const env = browser();
    return env === undefined ? undefined : parseSessionHash(env.location.hash);
}
/**
 * Make the page address follow the selection. The first link of a page
 * replaces the entry (no extra back step); later changes push one, so back
 * returns to the previous conversation. `undefined` removes the link.
 * @param selection - the current selection, or `undefined` once it was cleared.
 */
export function writeLocationSelection(selection) {
    const env = browser();
    if (env === undefined)
        return;
    const { location, history } = env;
    const current = location.hash;
    if (selection === undefined) {
        if (parseSessionHash(current) === undefined)
            return;
        history.replaceState(history.state, '', `${location.pathname}${location.search}`);
        return;
    }
    const next = formatSessionHash(selection);
    if (next === current)
        return;
    const url = `${location.pathname}${location.search}${next}`;
    if (parseSessionHash(current) === undefined)
        history.replaceState(history.state, '', url);
    else
        history.pushState(history.state, '', url);
}
/**
 * Follow address changes made by the browser (back/forward, a pasted link).
 * @param onNavigate - receives the selection the new address links to.
 * @returns disposer removing the listeners.
 */
export function watchLocationSelection(onNavigate) {
    if (typeof window === 'undefined')
        return () => { };
    const listener = () => {
        const linked = parseSessionHash(window.location.hash);
        if (linked !== undefined)
            onNavigate(linked);
    };
    window.addEventListener('popstate', listener);
    window.addEventListener('hashchange', listener);
    return () => {
        window.removeEventListener('popstate', listener);
        window.removeEventListener('hashchange', listener);
    };
}
//# sourceMappingURL=location.js.map